import React, { useState } from 'react';
import { ArrowRight, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LogoMark } from './brand/Logo';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Field } from './ui/field';
import { Alert } from './ui/alert';
import { Spinner } from './ui/spinner';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signIn } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        setError(error.message);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-12">
      {/* Ambient brand glow behind the card */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-1/2 size-[36rem] -translate-x-1/2 -translate-y-[60%] rounded-full bg-brand/15 blur-[120px]"
      />

      <main className="relative w-full max-w-sm animate-pop-in">
        <div className="mb-8 flex flex-col items-center text-center">
          <LogoMark className="mb-5 size-12 drop-shadow-[0_8px_24px_rgb(155_109_255/0.45)]" />
          <h1 className="text-2xl font-semibold tracking-tight text-fg">Welcome back</h1>
          <p className="mt-1.5 text-sm text-fg-muted">Sign in to track your hours and earnings.</p>
        </div>

        <div className="rounded-2xl border border-line-strong bg-surface/80 p-6 shadow-pop backdrop-blur-xl">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Field label="Email" htmlFor="email">
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                disabled={loading}
                leading={<Mail className="size-4" />}
                className="pl-9"
              />
            </Field>

            <Field label="Password" htmlFor="password">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                disabled={loading}
                leading={<Lock className="size-4" />}
                className="pl-9"
                trailing={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-7"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </Button>
                }
              />
            </Field>

            {error && <Alert tone="danger">{error}</Alert>}

            <Button type="submit" size="lg" disabled={loading} className="mt-1 w-full">
              {loading ? (
                <>
                  <Spinner className="size-4 border-white/30 border-t-white" label="Signing in" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight />
                </>
              )}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};
